import type { Meta } from "@/types/Meta";
import type { RankedUser } from "@/types/WebUser";
import romanApi from "@roman/shared/lib/net/romanApi";
import useSWRInfinite, { type SWRInfiniteConfiguration } from "swr/infinite";

type GetActiveLeaderboardRankResponse = {
  users: RankedUser[];
  myUser: RankedUser;
  meta: Meta;
};

export const DEFAULT_PER_PAGE = 50;

export type UseGetActiveLeaderboardRankProps = {
  swrOptions?: SWRInfiniteConfiguration;
  fetcher?: (url: string) => Promise<GetActiveLeaderboardRankResponse>;
};

export const createDefaultLeaderboardFetcher = () => {
  return (url: string) => romanApi.get<GetActiveLeaderboardRankResponse>(url);
};

const getKey = (pageIndex: number, previousPageData: any) => {
  if (previousPageData && !previousPageData.users?.length) {
    return null; // reached the end
  }
  return `/leaderboard?page=${pageIndex + 1}&per=${DEFAULT_PER_PAGE}`;
};

export const useGetActiveLeaderboardRank = ({ swrOptions, fetcher }: UseGetActiveLeaderboardRankProps) => {
  const leaderboardFetcher = fetcher || createDefaultLeaderboardFetcher();

  const options: SWRInfiniteConfiguration = {
    revalidateFirstPage: false,
    ...swrOptions,
  };

  const swrQuery = useSWRInfinite(getKey, (url: string) => leaderboardFetcher(url), options);

  const users = swrQuery.data ? swrQuery.data.flatMap((pageData) => pageData.users) : [];

  return {
    ...swrQuery,
    users,
    page: swrQuery.size,
    setPage: swrQuery.setSize,
  };
};
